import React, { Fragment, useCallback, useContext } from 'react';
import { Button, Card, Descriptions, message, Modal, Space, Spin } from 'antd';
import { useParams } from 'react-router-dom';

import { useDnsTask } from '../hooks/use-dns-task';
import { CustomsService } from '../services';
import * as Icons from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useBackgroundNavigate } from '@shared/hooks';
import { ParcelContext } from '@modules/parcels/contexts';
import { localURLMaker } from '@shared/utils';

export const CustomsDnsTaskDetails = () => {
  const navigate = useBackgroundNavigate();
  const params = useParams<{ id: string }>();
  const { data, loading, reFetch } = useDnsTask(params.id ?? '');
  const { currentParcel } = useContext(ParcelContext);
  const { t } = useTranslation();


  const accept = useCallback(async () => {
    const action = async () => {
      const result = await CustomsService.Dns.accept(params.id ?? '');

      if (result.status === 200) {
        message.success('Bağlama təsdiq edildi.');
        await reFetch();
      } else if (result.status === 411) {
        const { id, reason } = result.data;

        navigate(localURLMaker('/customs/dns/:id/details', { id }));

        Modal.warning({ title: t('common.errors.operationFailed'), content: reason });
      } else if (result.status === 422) {
        const { error } = result.data
        message.error(error.join('. '));
      } else {
        message.error(result.data);
      }
    };

    Modal.confirm({
      title: t('common.actions.accept'),
      content: t('declarations.accept'),
      onOk: action,
    });
  }, [navigate, params.id, reFetch, t]);

  return (
    <Modal
      open
      width={768}
      footer={null}
      title={data?.declaration.trackCode ? t('dns.notificationTitleWithTrackCode', { trackCode: data.declaration.trackCode }) : undefined}
      onCancel={() => navigate('/customs/dns')}
    >
      <Spin spinning={loading}>
        <div>
          {!!data && (
            <Fragment>
              <Space size={8}>
                <Button disabled={data.status.id !== 37 || !currentParcel} onClick={accept} icon={<Icons.CheckCircleOutlined />}>
                  {t('common.actions.accept')}
                </Button>
              </Space>
              <Card bodyStyle={{ padding: 0 }} style={{ marginTop: 12 }} type='inner' title='Bildiriş' size='small'>
                <Descriptions size='small' column={{ sm: 2, xs: 1 }} colon={true} bordered={true}>
                  <Descriptions.Item label={t('common.fields.id')}>{data.id}</Descriptions.Item>
                  <Descriptions.Item label={t('common.fields.type')}>{data.action}</Descriptions.Item>
                  <Descriptions.Item label={t('common.fields.status.name')}>{data.status.name}</Descriptions.Item>
                  <Descriptions.Item label={t('common.fields.createdAt')}>{data.createdAt}</Descriptions.Item>
                </Descriptions>
              </Card>
              <Card bodyStyle={{ padding: 0 }} style={{ marginTop: 24 }} type='inner' title='Bağlama' size='small'>
                <Descriptions size='small' column={{ sm: 2, xs: 1 }} colon={true} bordered={true}>
                  <Descriptions.Item label={t('common.fields.user.name')}>{data.declaration.user.name}</Descriptions.Item>
                  <Descriptions.Item label={t('common.fields.trackCode')}>{data.declaration.trackCode}</Descriptions.Item>
                  <Descriptions.Item label={t('declarations.fields.globalTrackCodeShort')}>{data.declaration.globalTrackCode}</Descriptions.Item>
                  <Descriptions.Item label={t('common.fields.status.name')}>{data.declaration.status.name}</Descriptions.Item>
                  <Descriptions.Item label={t('declarations.fields.productType.name')}>{data.declaration.productType.name}</Descriptions.Item>
                  <Descriptions.Item label={t('common.fields.quantity')}>{data.declaration.quantity} ədəd</Descriptions.Item>
                  <Descriptions.Item label={t('common.fields.weight')}>
                    {!!data.declaration.weight && t('common.weightWithKG', { weight: data.declaration.weight })}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Fragment>
          )}
        </div>
      </Spin>
    </Modal >
  );
};
